import React, { useContext, useEffect, useState } from 'react';
import axiosInstance from '../../Services/axios';
import ApplicationContext from '../../Framework/Context/ApplicationContext';

const TYPES = [
    { key: 'price', label: 'Price units (currency)' },
    { key: 'size',  label: 'Size units' },
];

const PcrUnitList = () => {
    const setPageInfo = useContext(ApplicationContext);
    const [units, setUnits] = useState([]);
    const [newValues, setNewValues] = useState({ price: '', size: '' });
    const [error, setError] = useState(null);

    useEffect(() => {
        setPageInfo({ title: 'PCR Units' });
        fetchUnits();
    }, []);

    const fetchUnits = () => {
        axiosInstance.get('/admin/pcr-units')
            .then(r => setUnits(r.data))
            .catch(() => setError('Loading failed'));
    };

    const unitsFor = (type) =>
        units.filter(u => u.type === type).sort((a, b) => a.sort_order - b.sort_order);

    const activeCountFor = (type) =>
        units.filter(u => u.type === type && u.is_active).length;

    const handleAdd = (type) => {
        const value = newValues[type].trim();
        if (!value) return;
        setError(null);
        axiosInstance.post('/admin/pcr-units', { type, value })
            .then(() => {
                setNewValues(prev => ({ ...prev, [type]: '' }));
                fetchUnits();
            })
            .catch(e => setError(e.response?.data?.message || 'An error occurred.'));
    };

    const handleToggleActive = (unit) => {
        setError(null);
        axiosInstance.put(`/admin/pcr-units/${unit.id}`, { is_active: !unit.is_active })
            .then(() => fetchUnits())
            .catch(e => setError(e.response?.data?.message || 'An error occurred.'));
    };

    const handleDeactivate = (unit) => {
        setError(null);
        axiosInstance.delete(`/admin/pcr-units/${unit.id}`)
            .then(() => fetchUnits())
            .catch(e => setError(e.response?.data?.message || 'An error occurred.'));
    };

    const handleActivate = (unit) => {
        setError(null);
        axiosInstance.put(`/admin/pcr-units/${unit.id}`, { is_active: true })
            .then(() => fetchUnits())
            .catch(e => setError(e.response?.data?.message || 'An error occurred.'));
    };

    const handleSetDefault = (unit) => {
        setError(null);
        axiosInstance.put(`/admin/pcr-units/${unit.id}`, { is_default: true })
            .then(() => fetchUnits())
            .catch(e => setError(e.response?.data?.message || 'An error occurred.'));
    };

    const handleMove = (type, index, direction) => {
        const list = unitsFor(type);
        const swapIndex = index + direction;
        if (swapIndex < 0 || swapIndex >= list.length) return;

        const order = list.map((u, i) => {
            if (i === index) return { id: u.id, sort_order: list[swapIndex].sort_order };
            if (i === swapIndex) return { id: u.id, sort_order: list[index].sort_order };
            return { id: u.id, sort_order: u.sort_order };
        });

        axiosInstance.post('/admin/pcr-units/reorder', { order })
            .then(() => fetchUnits())
            .catch(e => setError(e.response?.data?.message || 'An error occurred.'));
    };

    return (
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '2rem' }}>
            <h1 style={{ fontSize: '1.5rem', marginBottom: '2rem' }}>PCR Units</h1>

            {error && (
                <p style={{ color: 'red', marginBottom: '1rem', padding: '8px 12px', background: '#fee2e2', borderRadius: 4 }}>
                    {error}
                </p>
            )}

            {TYPES.map(({ key, label }) => {
                const list = unitsFor(key);
                const activeCount = activeCountFor(key);
                return (
                    <section key={key} style={{ marginBottom: '3rem' }}>
                        <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem', fontWeight: 600 }}>
                            {label}
                        </h2>

                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                                    <th style={{ padding: '6px 8px' }}>Value</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Default</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Active</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Order</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((unit, idx) => {
                                    const isOnlyActiveDefault = unit.is_default && activeCount === 1;
                                    return (
                                        <tr
                                            key={unit.id}
                                            style={{
                                                borderBottom: '1px solid #e5e7eb',
                                                opacity: unit.is_active ? 1 : 0.45,
                                            }}
                                        >
                                            <td style={{ padding: '8px', fontWeight: unit.is_default ? 700 : 400 }}>
                                                {unit.value}
                                                {unit.is_default && (
                                                    <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#6b7280' }}>
                                                        (default)
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                {unit.is_default
                                                    ? <span title="Default" style={{ color: '#f59e0b' }}>★</span>
                                                    : unit.is_active && (
                                                        <button
                                                            onClick={() => handleSetDefault(unit)}
                                                            title="Set as default"
                                                            style={btnStyle('#3b82f6')}
                                                        >
                                                            ★
                                                        </button>
                                                    )
                                                }
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                {unit.is_active ? (
                                                    <button
                                                        onClick={() => handleDeactivate(unit)}
                                                        disabled={isOnlyActiveDefault}
                                                        style={btnStyle(isOnlyActiveDefault ? '#9ca3af' : '#ef4444')}
                                                        title={isOnlyActiveDefault
                                                            ? 'Set another unit as default first'
                                                            : 'Deactivate'}
                                                    >
                                                        Deact.
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleActivate(unit)}
                                                        style={btnStyle('#22c55e')}
                                                        title="Activate"
                                                    >
                                                        Act.
                                                    </button>
                                                )}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleMove(key, idx, -1)}
                                                    disabled={idx === 0}
                                                    style={arrowBtnStyle(idx === 0)}
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    onClick={() => handleMove(key, idx, 1)}
                                                    disabled={idx === list.length - 1}
                                                    style={arrowBtnStyle(idx === list.length - 1)}
                                                >
                                                    ▼
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                type="text"
                                placeholder={`New ${key === 'price' ? 'currency' : 'unit'} (e.g. ${key === 'price' ? 'CHF' : 'oz'})`}
                                value={newValues[key]}
                                onChange={e => setNewValues(prev => ({ ...prev, [key]: e.target.value }))}
                                onKeyDown={e => e.key === 'Enter' && handleAdd(key)}
                                style={{
                                    border: '1px solid #d1d5db',
                                    borderRadius: 4,
                                    padding: '6px 10px',
                                    fontSize: '0.9rem',
                                    width: 160,
                                }}
                            />
                            <button
                                onClick={() => handleAdd(key)}
                                style={btnStyle('#6366f1')}
                            >
                                + Add
                            </button>
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

const btnStyle = (bg) => ({
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '4px 10px',
    cursor: 'pointer',
    fontSize: '0.8rem',
});

const arrowBtnStyle = (disabled) => ({
    background: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    padding: '2px 7px',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.3 : 1,
    marginRight: 2,
    fontSize: '0.75rem',
});

export default PcrUnitList;
